import type { CurrencyItem, IPriceAmount, IPriceInfoList } from '@/api/types/currency-price';
import type { IProductSensorData, ProductsListCorrelationItem } from '@/api/types/product';
import { getProductImage } from '@/assets/js/utils';
import { useStore } from '@/store';

/** 商品卡片标签项 */
export interface ProductCardTagItem {
  content?: string;
  fill?: string;
  has_content?: number;
  has_fill?: number;
  has_stroke?: number;
  stroke?: string;
  color?: string;
}

/** 商品卡片标签返回值 */
export interface GetProductCardTagsResult {
  tagList: ProductCardTagItem[];
  tagsForSensors: string[];
}

/** 商品详情（标签用，仅列出用到的字段） */
interface ProductDetailForTags {
  tags?: string[];
  match_label_lists?: { card_label?: Array<{ content?: string; [key: string]: unknown }> };
  display_discount_percent?: number | string;
  stock?: number;
  status?: number;
  [key: string]: unknown;
}

/**
 * 获取商品卡片标签列表及神策埋点用标签
 * @param params.productDetail - 商品详情对象
 * @param params.maxTagCount - 最大标签数量
 * @param params.isHalfPrice - 是否半价标签
 * @param params.showNonRxTag - 是否展示 Non-Rx 标签
 * @returns tagList、tagsForSensors
 */
export function getProductCardTags({
  productDetail,
  maxTagCount = 2,
  isHalfPrice,
  showNonRxTag = false,
}: {
  productDetail: ProductDetailForTags | null | undefined;
  maxTagCount?: number;
  isHalfPrice?: boolean;
  showNonRxTag?: boolean;
}): GetProductCardTagsResult {
  if (!productDetail || typeof productDetail !== 'object') {
    return {
      tagList: [],
      tagsForSensors: [],
    };
  }

  const store = useStore();

  // 确保 maxTagCount 是有效的正整数
  const validMaxTagCount = Math.max(1, Math.floor(Number(maxTagCount) || 2));

  // 确保 isHalfPrice 是布尔值
  const isValidHalfPrice = Boolean(isHalfPrice);

  let tagList: ProductCardTagItem[] = [];

  try {
    // 动态获取 半价/N折 折扣比例
    if (isValidHalfPrice && store.halfPriceRule?.discount_ratio_text) {
      tagList = [
        {
          content: store.halfPriceRule.discount_ratio_text,
        },
      ];
    } else if (Array.isArray(productDetail.tags) && productDetail.tags.length > 0) {
      // 特殊处理TBG页面的标签
      tagList = productDetail.tags
        .filter((tag) => tag != null && typeof tag === 'string' && tag.trim().length > 0)
        .map((tag) => {
          return {
            content: String(tag).trim(),
            fill: '#292928',
            has_content: 1,
            has_fill: 1,
            has_stroke: 2,
            stroke: '',
            color: '#dbff2a',
          };
        });
    } else if (
      productDetail.match_label_lists?.card_label &&
      Array.isArray(productDetail.match_label_lists.card_label) &&
      productDetail.match_label_lists.card_label.length > 0
    ) {
      // 过滤掉无效的标签项（必须包含 content 属性）
      let list = productDetail.match_label_lists.card_label.filter(
        (item) => item && typeof item === 'object' && item.content != null,
      );

      // 处理折扣百分比
      const discountPercent = productDetail.display_discount_percent;
      const hasValidDiscount =
        discountPercent != null &&
        String(discountPercent).trim() !== '' &&
        String(discountPercent).trim() !== '0' &&
        !isNaN(parseFloat(String(discountPercent)));

      if (!hasValidDiscount) {
        list = list.filter((item) => {
          const content = String(item.content || '');
          return !content.includes('${dis}');
        });
      } else {
        const discountValue = parseFloat(String(discountPercent));
        if (!isNaN(discountValue)) {
          const discountText = discountValue.toFixed(0);
          list = list.map((item) => {
            const content = String(item.content || '');
            return {
              ...item,
              content: content.replace('${dis}', discountText),
            };
          });
        }
      }

      if (list.length > 0) {
        tagList = list.length <= validMaxTagCount ? list : list.slice(0, validMaxTagCount);
      }
    }
  } catch (error) {
    // 如果处理过程中出现错误，返回空数组
    console.warn('getProductCardTags error:', error);
    tagList = [];
  }

  // 生成神策买点格式的标签，确保 content 存在
  const tagsForSensors = tagList
    .filter((item) => item && item.content != null)
    .map((item) => `${String(item.content)}@`);

  if ((productDetail.stock ?? 1) <= 0) {
    tagsForSensors.push('Sold Out@');
  }

  if (productDetail.status === 2) {
    tagsForSensors.push('Not Available@');
  }

  if (showNonRxTag) {
    tagsForSensors.push('Non-Rx@');
  }

  return {
    tagList,
    tagsForSensors,
  };
}

/** 价格生成函数类型 */
type GenPriceFn = (priceInfo: IPriceInfoList, currency?: string) => IPriceAmount;

interface GetProductCardSensorsTrackParamsOptions {
  product_display_label?: string[];
  display_color_num?: number | null;
  showMaterialImg?: boolean;
  isHalfPrice?: boolean;
  showNonRxTag?: boolean;
  mktName?: string;
}

/**
 * 获取商品卡片的埋点参数（神策等）
 * @param product - 商品对象
 * @param clickIndex - 点击索引
 * @param currency - 货币对象 { name, exchange_rate }
 * @param genPrice - 价格处理函数 (priceInfo, currency?) => { price, sell_price }
 * @param options - 可选：product_display_label、display_color_num、
 *   showMaterialImg、isHalfPrice、showNonRxTag
 * @returns 埋点参数字典
 */
export function getProductCardSensorsTrackParams(
  product: ProductsListCorrelationItem,
  clickIndex: number,
  currency: CurrencyItem,
  genPrice: GenPriceFn,
  options: GetProductCardSensorsTrackParamsOptions = {},
): IProductSensorData {
  const {
    product_display_label: customProductDisplayLabel,
    display_color_num = null,
    showMaterialImg = false,
    isHalfPrice = false,
    showNonRxTag = false,
    mktName = '',
  } = options;

  // 获取商品标签
  let product_display_label;
  if (customProductDisplayLabel !== undefined) {
    product_display_label = customProductDisplayLabel;
  } else {
    product_display_label = getProductCardTags({
      productDetail: product,
      isHalfPrice,
      showNonRxTag,
    }).tagsForSensors;
  }

  const productImages = getProductImage({
    product,
    showMaterialImg,
  });

  const priceInfo = product.price_info ?? [];
  const displayPriceInfo = product.display_price_info ?? [];

  return {
    mkt_name: mktName,
    product_display_label,
    product_index: clickIndex,
    business_sku: product.business_sku,
    shop_sku: product.shop_sku,
    product_sku: product.sku,
    product_id: String(product.id ?? ''),
    product_name: product.short_name,
    product_class_name: product.products_category,
    product_review_num: parseInt(product.review_num ?? '0'),
    product_price: parseFloat(genPrice(priceInfo).price ?? '0'),
    current_product_price: parseFloat(genPrice(priceInfo).sell_price ?? '0'),
    base_product_price: parseFloat(genPrice(priceInfo, 'USD').price ?? '0'),
    base_current_product_price: parseFloat(genPrice(priceInfo, 'USD').sell_price ?? '0'),
    include_lens_package_product_price: parseFloat(genPrice(displayPriceInfo).price ?? '0'),
    include_lens_package_current_product_price: parseFloat(
      genPrice(displayPriceInfo).sell_price ?? '0',
    ),
    include_lens_package_base_product_price: parseFloat(
      genPrice(displayPriceInfo, 'USD').price ?? '0',
    ),
    include_lens_package_base_current_product_price: parseFloat(
      genPrice(displayPriceInfo, 'USD').sell_price ?? '0',
    ),
    currency: currency.name,
    product_collect_num: product.wish_num,
    dollar_exchange_rate: Number(currency.exchange_rate),
    product_rating: parseFloat(product.spu_review_stars ?? ''),
    first_picture_url: productImages.mainImage.src,
    second_picture_url: productImages.hoverImage.src,
    display_color_num,
    recall_group_type: product?.recall_group_type ?? undefined,
    fine_ranking_type: product?.fine_ranking_type ?? undefined,
  };
}
